import { useEffect, useState } from "react"
import { faCheckCircle, faEye } from "@fortawesome/free-solid-svg-icons"

import Toggle from "components/Toggle"
import ButtonPrimary from "components/ButtonPrimary"
import Loading from "components/Loading"

import { PERMISSIONS, hasFeature } from "lib/user-utils"

import { discectError, ucwords } from "lib/utils"

import api from 'lib/api'

import styles from "./styles.module.scss"

const Permissions = ({ agency, employees }) => {
  const [isLoading, setIsLoading] = useState(null)
  const [error, setError] = useState(null)
  const [panelType, setPanelType] = useState('GLOBAL')
  const [isGlobalChanged, setIsGlobalChanged] = useState(false)
  const [isEmployeesChanged, setIsEmployeesChanged] = useState(false)
  const [permissions, setPermissions] = useState(null)
  const [employeePermissionChanges, setEmployeePermissionChanges] = useState({})
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null)

  const { restrictedPermissions } = agency

  useEffect(() => {
    if (!permissions) setPermissions(() => restrictedPermissions || [])
  }, [permissions, restrictedPermissions])

  const _savePermissions = async () => {
    setError(null)
    setIsLoading(true)
    try {
      await api.bondsman.updatePermissions({ permissions })
      setIsGlobalChanged(false)
    } catch(e) {
      setError(discectError(e))
    }
    setIsLoading(false)
  }

  const _savePermissionsEmployees = async () => {
    setError(null)
    setIsLoading(true)
    try {
      await api.bondsman.updatePermissionsEmployees({ permissions: employeePermissionChanges })
      setIsGlobalChanged(false)
    } catch(e) {
      setError(discectError(e))
    }
    setIsLoading(false)
  }

  const _getUserTag = (type) => {
    let tag = <span className="tag tag--blue">Agent</span>
    if (['SUPER','OWNER'].indexOf(type) >= 0) {
      tag = <span className="tag tag--teal">Owner</span>
    } else if (['ADMIN'].indexOf(type) >= 0) {
      tag = <span className="tag tag--teal">Manager</span>
    } else if (['ASSISTANT'].indexOf(type) >= 0) {
      tag = <span className="tag tag--teal">Assistant</span>
    } else if (['FINANCE'].indexOf(type) >= 0) {
      tag = <span className="tag tag--teal">Finance</span>
    } else if (['COLLECTOR'].indexOf(type) >= 0) {
      tag = <span className="tag tag--teal">Collector</span>
    } else if (['RECOVERY'].indexOf(type) >= 0) {
      tag = <span className="tag tag--teal">Recovery</span>
    }
    return <div className="tags">{tag}</div>
  }

  const _onChangePermission = (id, isOn) => {
    let newPermissions = [...permissions]
    if (isOn) {
      newPermissions.push(id)
    } else {
      newPermissions = newPermissions.filter(itm => itm !== id)
    }
    setPermissions(() => newPermissions)
    setIsGlobalChanged(true)
  }

  const _onChangeEmployeePermission = (employeeId, id, isOn) => {
    let newPermissions = { ...employeePermissionChanges }
    if (!newPermissions[employeeId]) {
      const employee = employees.find(e => e.id === employeeId)
      newPermissions[employeeId] = employee?.permissions || []
    }
    if (isOn) {
      newPermissions[employeeId].push(id)
    } else {
      newPermissions[employeeId] = newPermissions[employeeId].filter(itm => itm !== id)
    }
    setEmployeePermissionChanges(() => newPermissions)
    setIsEmployeesChanged(true)
  }

  const _getGlobalPermissions = () => {
    if (permissions) {
      return PERMISSIONS
        .filter(itm => {
          if (itm.features.length) {
            return hasFeature(agency, itm.features)
          }
          return true
        })
        .map((group, groupKey) => {
          return (
            <div key={groupKey} className={styles.permissions__group}>
              <h5 className="rad">{group.title}</h5>
              <div className={styles.permissions__group__rows}>
                {group.options.filter(itm => {
                  if (itm.features.length) {
                    return hasFeature(agency, itm.features)
                  }
                  return true
                }).map((itm, key) => {
                  return (
                    <div key={`${groupKey}__${key}`} className={styles.permissions__permission}>
                      <div className={styles.permissions__permission__left}>
                        <h4>{itm.title}</h4>
                        <p>{itm.descriptionAlt}</p>
                      </div>
                      <div className={styles.permissions__permission__right}>
                        <Toggle id={itm.type} name={itm.type} checked={(permissions.indexOf(itm.type) >= 0)} isYesNo onChange={_onChangePermission} />
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )
        })
    }
    return null
  }

  const _getAllPermissions = () => {
    let allPermissions = []
    PERMISSIONS.forEach(group => allPermissions = allPermissions.concat(group.options))
    return allPermissions
  }

  const _getEmployeesList = () => {
    const allPermissions = _getAllPermissions()
    return employees.map((employee, key) => {
      let allowedPermissions = [...employee.permissions]
      if (employeePermissionChanges[employee.id]) {
        allowedPermissions = [...allowedPermissions, ...employeePermissionChanges[employee.id]]
      }

      return (
        <div key={`employee_${key}`} className={styles.employees__employee}>
          <div className={styles.employees__employee__top}>
            <div className={styles.employees__employee__top__left}>
              <h4>
                {ucwords(`${employee.nameFirst} ${employee.nameLast}`.toLowerCase())}
                {_getUserTag(employee.type)}
              </h4>
              <p>Permissions: {allowedPermissions.length} Permissions Active</p>
            </div>
            <div className={styles.employees__employee__top__right}>
              <ButtonPrimary small color="navy" icon={faEye} onClick={() => setSelectedEmployeeId(() => employee.id)}>View</ButtonPrimary>
            </div>
          </div>
          {employee.id === selectedEmployeeId && (
            <div className={styles.employees__employee__btm}>
              {permissions?.length > 0 && permissions.map((p, pKey) => {
                const perm = allPermissions.find(itm => itm.type === p)
                if (perm && hasFeature(agency, perm.features)) {
                  return (
                    <div key={`${key}_${pKey}`} className={styles.employees__employee__permission}>
                      <div>
                        <h4>{perm.title}</h4>
                        <p>{perm.description}</p>
                      </div>
                      <Toggle 
                        isYesNo
                        id={perm.type}
                        name={perm.type}
                        checked={(allowedPermissions.indexOf(perm.type) >= 0)}
                        onChange={(id, isOn) => _onChangeEmployeePermission(employee.id, id, isOn)}
                      />
                    </div>
                  )
                }
                console.error('Not Found: ', p)
                return null
              }).filter(itm => !!itm)}
            </div>
          )}
        </div>
      )
    })
  }

  return (
    <div className={styles.permissions__container}>
      <header>
        <p className="instructions">Toggle permissions on to restrict them.</p>
      </header>
      <div className={styles.permissions__toggle}>
        <button onClick={() => setPanelType('GLOBAL')} className={(panelType === 'GLOBAL') ? styles.active : null}>Global Permissions</button>
        <button onClick={() => setPanelType('EMPLOYEE')} className={(panelType === 'EMPLOYEE') ? styles.active : null}>Employee Permissions</button>
      </div>
      {error && <p className="error">{error}</p>}
      {panelType === 'GLOBAL' && (
        <div className={styles.permissions}>
          {_getGlobalPermissions()}
          {isGlobalChanged && (
            <div className={styles.saveActons}>
              <ButtonPrimary small color="navy" icon={faCheckCircle} onClick={_savePermissions}>Save</ButtonPrimary>
            </div>
          )}
        </div>
      )}
      {panelType === 'EMPLOYEE' && (
        <div className={styles.employees}>
          {isEmployeesChanged && (
            <div className={`${styles.saveActons} ${styles.saveActons__btm}`}>
              <ButtonPrimary small color="navy" icon={faCheckCircle} onClick={_savePermissionsEmployees}>Save</ButtonPrimary>
            </div>
          )}
          {_getEmployeesList()}
          {isEmployeesChanged && (
            <div className={`${styles.saveActons} ${styles.saveActons__top}`}>
              <ButtonPrimary small color="navy" icon={faCheckCircle} onClick={_savePermissionsEmployees}>Save</ButtonPrimary>
            </div>
          )}
        </div>
      )}
      {isLoading && <Loading />}
    </div>
  )
}

export default Permissions
